/**
 * bankMatching/mappers — Firestore-Shapes → Engine-Shapes (§9.2 / §11 / §15).
 *
 * EINZIGER Ort mit `eurosToCents` auf `invoices.totalAmount` (§15). Die
 * Skonto-/DueDate-/InvoiceDate-Lesart ist 1:1 aus `OpenItemsPage.tsx`
 * übernommen (Code = Source-of-Truth; §15 warnt vor Skonto-Feldnamen-Drift:
 * INVOICES nutzen `rawData.skonto.percentage/.days`, NICHT `.percent`).
 *
 * Pure, ohne Firestore-Imports: `Timestamp`-Werte werden defensiv über ein
 * `.toDate()`-Duck-Typing behandelt (kein `firebase/firestore`-Import in dieser
 * reinen Datei — nur Browser-/jest-jsdom-neutraler Code).
 *
 * @module bankMatching/mappers
 */

import { eurosToCents } from '../moneyUtils';
import { computeOpenItemStatus } from '../openItemStatus';
import { Payment } from '../../types/paymentTypes';
import { DunningEvent } from '../../types/dunningTypes';
import { Expense } from '../../types/expenseTypes';
import { ReceivedInvoice } from '../../types/receivedInvoiceTypes';
import { InvoiceDoc, MatchableInvoice, MatchableExpense } from './types';
import { normalizeIban } from './normalize';

// ─────────────────────────────────────────────────────────────────────────
// Datums-Helfer (Timestamp-tolerant, ohne firebase-Import)
// ─────────────────────────────────────────────────────────────────────────

/** Firestore-`Timestamp`-Duck-Type: Objekt mit `toDate(): Date`. */
interface TimestampLike {
  toDate: () => Date;
}

function isTimestampLike(v: unknown): v is TimestampLike {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { toDate?: unknown }).toDate === 'function'
  );
}

/** Formatiert ein `Date` als lokales `YYYY-MM-DD` (Muster OpenItemsPage). */
function dateToISO(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Extrahiert `YYYY-MM-DD` aus String, `Timestamp`-like oder `null`/undefined.
 * Spiegelt `OpenItemsPage.toDateISO`.
 */
function toDateISO(value: unknown): string | null {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value.slice(0, 10);
  if (isTimestampLike(value)) return dateToISO(value.toDate());
  return null;
}

/**
 * dueDate-Extraktion (Priorität wie OpenItemsPage): Top-Level `dueDate` →
 * `rawData.dueDate` → `rawData.paymentTerms.dueDate` → `null`.
 */
function extractDueDate(inv: InvoiceDoc): string | null {
  if (inv.dueDate) return inv.dueDate;
  if (inv.rawData?.dueDate) return inv.rawData.dueDate;
  if (inv.rawData?.paymentTerms?.dueDate) return inv.rawData.paymentTerms.dueDate;
  return null;
}

/**
 * invoiceDate-Extraktion (Priorität wie OpenItemsPage): Top-Level `invoiceDate`
 * (Timestamp/String) → `rawData.invoiceDate` → `todayISO` (sicherer Fallback).
 */
function extractInvoiceDate(inv: InvoiceDoc, todayISO: string): string {
  const topLevel = toDateISO(inv.invoiceDate);
  if (topLevel) return topLevel;
  if (inv.rawData?.invoiceDate) return inv.rawData.invoiceDate.slice(0, 10);
  return todayISO;
}

/** ISO-Datum der jüngsten Zahlung (Muster `OpenItemsPage.latestPaymentDate`). */
function latestPaymentDate(payments: Payment[], todayISO: string): string | null {
  if (payments.length === 0) return null;
  const dates = payments.map((p) => {
    const raw = p.paidAt;
    if (typeof raw === 'string') return raw.slice(0, 10);
    if (isTimestampLike(raw)) return dateToISO(raw.toDate());
    return todayISO;
  });
  return dates.slice().sort().at(-1) ?? null;
}

// ─────────────────────────────────────────────────────────────────────────
// toMatchableInvoices (§9.2 / §8.2)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Mappt Firestore-`invoices` (+ Payments + Dunning-Events) auf
 * `MatchableInvoice[]`. EINZIGER `eurosToCents`-Ort auf `totalAmount` (§15).
 *
 * Ausgeschlossen: stornierte/Storno-Rechnungen (`isCancelled`/`isCancellation`)
 * und Nicht-EUR-Rechnungen (defensiv — die Cent-Konvertierung eines
 * Fremdwährungs-Floats wäre falsch). Paid-Rechnungen bleiben im Ergebnis; die
 * Engine filtert den Kandidatenraum über `openAmountCents > 0`.
 *
 * `hasActiveDunning` (§8.2/§9.2): ≥1 `dunning_events`-Doc zur invoiceId UND
 * `openAmountCents > 0`. `dunningLevel`: höchste Mahnstufe der Events (sonst `null`).
 *
 * @param invoices - Firestore-Rechnungs-Dokumente (Minimal-Shape).
 * @param paymentsByInvoice - Payments je invoiceId (`getPaymentsForUser`, gruppiert).
 * @param dunningEventsByInvoice - Dunning-Events je invoiceId (`getAllDunningEventsForUser`).
 * @param todayISO - heutiges Datum `YYYY-MM-DD` (Reinheit).
 * @returns `MatchableInvoice[]` (Reihenfolge = Eingabe-Reihenfolge der aktiven Rechnungen).
 * @throws {Error} via `eurosToCents`, wenn `totalAmount` nicht endlich ist (fail-closed).
 */
export function toMatchableInvoices(
  invoices: InvoiceDoc[],
  paymentsByInvoice: Map<string, Payment[]>,
  dunningEventsByInvoice: Map<string, DunningEvent[]>,
  todayISO: string,
): MatchableInvoice[] {
  const result: MatchableInvoice[] = [];

  for (const inv of invoices) {
    if (inv.isCancelled || inv.isCancellation) continue;
    if (inv.currency != null && inv.currency !== 'EUR') continue;

    const storedGrandTotalCents = eurosToCents(inv.totalAmount ?? 0);
    const payments = paymentsByInvoice.get(inv.id) ?? [];
    const paymentsCents = payments.map((p) => p.amountCents);

    const invoiceDateISO = extractInvoiceDate(inv, todayISO);
    const dueDateISO = extractDueDate(inv);
    const paymentDateISO = latestPaymentDate(payments, todayISO);

    // §15-Lesart (INVOICES): rawData.skonto.percentage / .days.
    const rawSkonto = inv.rawData?.skonto;
    const skontoPercentage =
      rawSkonto?.percentage != null ? rawSkonto.percentage : null;
    const skontoDays = rawSkonto?.days != null ? rawSkonto.days : null;

    const computed = computeOpenItemStatus({
      storedGrandTotalCents,
      paymentsCents,
      invoiceDateISO,
      paymentDateISO,
      dueDateISO,
      todayISO,
      skontoPercentage,
      skontoDays,
    });

    const events = dunningEventsByInvoice.get(inv.id) ?? [];
    const openAmountCents = computed.openAmountCents;
    const hasActiveDunning = events.length > 0 && openAmountCents > 0;
    const dunningLevel =
      events.length > 0
        ? events.reduce((max, e) => (e.level > max ? e.level : max), events[0].level)
        : null;

    result.push({
      invoiceId: inv.id,
      invoiceNumber: inv.invoiceNumber,
      buyerName: inv.buyerName,
      invoiceDateISO,
      dueDateISO,
      storedGrandTotalCents,
      openAmountCents,
      skontoPercentage,
      skontoDays,
      hasActiveDunning,
      dunningLevel,
    });
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────
// toMatchableExpenses (§9.2 / §8.2)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Extrahiert die erste befüllte, normalisierte IBAN aus den `paymentMeans`
 * eines `received_invoices`-Dokuments (§15).
 */
function firstSupplierIban(received: ReceivedInvoice | undefined): string | null {
  if (!received) return null;
  const means = received.data?.paymentMeans ?? [];
  for (const m of means) {
    const iban = normalizeIban(m.iban ?? null);
    if (iban != null) return iban;
  }
  return null;
}

/**
 * Mappt Firestore-`expenses` (+ `received_invoices`-IBAN-Join) auf
 * `MatchableExpense[]`.
 *
 * Ausgeschlossen: Typ-381-Gutschriften (`documentTypeCode === '381'`) — deren
 * negativer `grossCents` gehörte auf die Eingangs-Seite (Erstattung), bewusste
 * v1-Grenze (§8.6). Der IBAN-Join läuft über `sourceReceivedInvoiceId`.
 * `alreadyMatched` kommt aus dem tx-seitigen Join (`matchedExpenseIdSet`).
 *
 * @param expenses - Firestore-Ausgaben-Dokumente.
 * @param receivedByExpenseSourceId - `received_invoices` je Doc-ID
 *   (= `expense.sourceReceivedInvoiceId`).
 * @param matchedExpenseIdSet - Set bereits abgeglichener Expense-IDs.
 * @returns `MatchableExpense[]` (Reihenfolge = Eingabe der Nicht-381-Ausgaben).
 */
export function toMatchableExpenses(
  expenses: Expense[],
  receivedByExpenseSourceId: Map<string, ReceivedInvoice>,
  matchedExpenseIdSet: Set<string>,
): MatchableExpense[] {
  const result: MatchableExpense[] = [];

  for (const exp of expenses) {
    if (exp.documentTypeCode === '381') continue; // Typ-381-Filter (§8.6).

    const received =
      exp.sourceReceivedInvoiceId != null
        ? receivedByExpenseSourceId.get(exp.sourceReceivedInvoiceId)
        : undefined;

    result.push({
      expenseId: exp.id,
      supplierName: exp.supplierName,
      invoiceNumber: exp.invoiceNumber,
      grossCents: exp.grossCents,
      expenseDateISO: exp.expenseDate,
      dueDateISO: exp.dueDate,
      supplierIban: firstSupplierIban(received),
      alreadyMatched: matchedExpenseIdSet.has(exp.id),
    });
  }

  return result;
}
